import type {
  FichaModelo,
  ModeloDePrecio,
  ParametroPrecio,
  PoliticaEfectiva,
  PublicarPolitica,
} from '../dominio/politicaPrecioApi';

/**
 * La lógica PURA de la pantalla de política de precio: qué habilita el botón de publicar, qué se
 * le dice al operador, y qué viaja en el cuerpo del `POST`.
 *
 * 🔑 **Vive en un módulo aparte de `Precio.tsx`, y no es sólo prolijidad.** `Dispatch.tsx` guarda
 * su equivalente dentro del `.tsx` y funciona, pero acá hay dos motivos concretos para separarlo:
 *
 *   1. **Es más lógica y más ramificada** (gate + validación por parámetro + tres motivos
 *      distintos para no poder publicar), y decide si la plataforma se COMPROMETE con el precio
 *      de un viaje. Merece poder ejercitarse sola.
 *   2. **Sin React ni JSX en el módulo, se puede correr con cualquier cosa que ejecute JavaScript.**
 *      Eso dejó de ser teórico el 2026-09-01: el runner de `apps/consola` no arranca en la
 *      máquina de desarrollo (el antivirus se lleva el binario nativo de rollup, ver el reporte
 *      de la rama), y esta separación fue lo que permitió **romper el arreglo a propósito y
 *      confirmar que el test falla** en vez de declarar la verificación por CI y seguir.
 */

/**
 * Topes espejados del DTO del backend. Se comprueban acá para que el 400 no sea la primera señal
 * que ve el operador — **no para reemplazar al backend**, que igual los hace cumplir y contra
 * todos los caminos, no sólo éste.
 */
const MAX_INT4 = 2_147_483_647;
const MAX_TOLERANCIA = 100;

/** Cómo se le presenta cada parámetro al operador. Qué parámetros van lo dice la ficha del modelo. */
export const CAMPOS: Record<
  ParametroPrecio,
  { etiqueta: string; unidad: string; ayuda: string; paso: string }
> = {
  ventanaPrecioSeg: {
    etiqueta: 'Tiempo para comparar precios',
    unidad: 'segundos',
    paso: '1',
    ayuda:
      'Cuánto espera el pasajero a que lleguen ofertas antes de elegir. NO es el tiempo que tiene el conductor para aceptar un viaje (ése vive en Parámetros de dispatch): confundirlas apura la decisión equivocada.',
  },
  maxOfertasPrecio: {
    etiqueta: 'Ofertas que se le muestran',
    unidad: 'ofertas',
    paso: '1',
    ayuda:
      'Más ofertas no es mejor: alguien parado en la calle bajo lluvia no compara doce números.',
  },
  toleranciaTechoPct: {
    etiqueta: 'Margen sobre el techo',
    unidad: '%',
    paso: '0.01',
    ayuda:
      'Cuánto se puede pasar del techo antes de tratarlo como sobrecobro. Existe porque el redondeo de efectivo es real: sin margen, un vuelto redondeado hacia arriba sería una infracción. 0 significa que el techo es el techo.',
  },
};

/**
 * El borrador guarda TEXTO, no números, y es a propósito: es lo que el `<input>` produce. Vaciar
 * un campo da `''` y pegar `1,5` da una cadena que no es número — las dos son entradas reales que
 * un `number` en el estado convertiría en `NaN` sin poder distinguir "vacío" de "basura".
 */
export interface BorradorPrecio {
  modelo: ModeloDePrecio;
  ventanaPrecioSeg: string;
  maxOfertasPrecio: string;
  toleranciaTechoPct: string;
}

/** Lo que gobierna el botón de publicar, el texto que lo acompaña y la advertencia previa. */
export interface EstadoPublicacionPrecio {
  /** La ficha del modelo elegido, o `null` si el catálogo no lo trae (no debería pasar). */
  ficha: FichaModelo | null;
  /** El modelo o algún parámetro difiere de lo que los flujos están usando ahora. */
  hayCambios: boolean;
  /** Los parámetros que el modelo USA están completos y dentro de rango. */
  valido: boolean;
  /** No hay política publicada: el precio lo gobierna el modelo por defecto, no una versión. */
  sinVersionVigente: boolean;
  /** El GATE del backend: la maquinaria que hace cumplir este modelo todavía no existe. */
  modeloBloqueado: boolean;
  puedePublicar: boolean;
  textoBoton: string;
  /** Aclaración al lado del botón, o `null` si no hay nada que aclarar. */
  ayuda: string | null;
  /** Advertencia de R-05 del modelo elegido, o `null`. Se muestra aunque se pueda publicar. */
  advertencia: string | null;
}

/**
 * `''` y una cadena que no es número dan los dos `null`. Los dos son inválidos, pero el primero
 * es el estado inicial normal y no debería leerse como un error del operador — por eso la ayuda
 * del formulario dice qué falta COMPLETAR en vez de qué está mal.
 */
function aNumero(texto: string): number | null {
  const limpio = texto.trim();
  if (limpio === '') return null;
  const n = Number(limpio);
  return Number.isFinite(n) ? n : null;
}

/** El valor parseado si es aceptable para ese parámetro, o `null`. Espeja las reglas del DTO. */
export function valorValido(parametro: ParametroPrecio, texto: string): number | null {
  const n = aNumero(texto);
  if (n === null) return null;

  if (parametro === 'toleranciaTechoPct') {
    // `Decimal(5,2)` + el CHECK `>= 0 AND <= 100` de la migración. Los 2 decimales son de la
    // columna: un tercer decimal lo rechaza `@IsNumber({ maxDecimalPlaces: 2 })` con 400.
    //
    // ⚠️ `Math.round(n * 100) === n * 100` es una comprobación de PUNTO FLOTANTE y por eso se
    // escribe así y no con `toFixed`: `7.255 * 100` da `725.4999...`, que redondea a 725 y no
    // coincide ⇒ rechazado, que es lo correcto. Un `String(n).split('.')` fallaría con notación
    // científica, y `toFixed(2)` redondearía en silencio un tercer decimal en vez de rechazarlo.
    if (n < 0 || n > MAX_TOLERANCIA) return null;
    return Math.round(n * 100) === n * 100 ? n : null;
  }

  // `ventanaPrecioSeg` y `maxOfertasPrecio` son `Int` con `@Min(1)`.
  if (!Number.isInteger(n) || n < 1 || n > MAX_INT4) return null;
  return n;
}

/**
 * Resuelve en un solo lugar si se puede publicar y qué se le dice al operador.
 *
 * 🔴 **`hayCambios` NO puede ser el único criterio, y el caso que lo rompe es TODA instalación
 * nueva.** La tabla `politicas_precio` nace VACÍA (la migración no inserta nada y no hay seed) ⇒
 * `vigente()` no encuentra fila y devuelve `origen: 'FALLBACK'` con el modelo por defecto ⇒ el
 * formulario arranca copiado de eso ⇒ el modelo elegido coincide con el efectivo ⇒ con la regla
 * ingenua `hayCambios` daría `false`, el botón quedaría deshabilitado y la pantalla diría "sin
 * cambios que guardar" justo debajo del banner rojo que pide publicar. **El operador que está de
 * acuerdo con el modelo por defecto no podría publicarlo como versión 1.** La única salida sería
 * elegir otro modelo, publicar, y volver a publicar el bueno: dos versiones basura en la tabla
 * que existe justamente para tener un historial limpio, y firmadas por alguien que no quería
 * cambiar nada.
 *
 * Este bug YA OCURRIÓ en la pantalla de parámetros de dispatch y se arregló ahí (ver
 * `calcularEstadoPublicacion` en `Dispatch.tsx` y su spec). Acá se escribe con el arreglo desde el
 * principio en vez de repetirlo. Con el fallback activo, publicar SIEMPRE es un cambio de estado
 * real aunque el modelo sea idéntico: se pasa de "ninguna versión gobierna el precio" a "existe
 * la v1", y recién desde ahí los viajes quedan atribuidos a una versión.
 *
 * ⚠️ **La validación NO se afloja, y el GATE tampoco**: `valido`, `modeloBloqueado` y `publicando`
 * siguen gobernando igual. Lo único que `sinVersionVigente` hace es sumarse a `hayCambios` como
 * MOTIVO para publicar.
 */
export function calcularEstadoPublicacionPrecio(entrada: {
  borrador: BorradorPrecio;
  efectivos: PoliticaEfectiva;
  catalogo: readonly FichaModelo[];
  proximaVersion: number;
  publicando: boolean;
}): EstadoPublicacionPrecio {
  const { borrador, efectivos, catalogo, proximaVersion, publicando } = entrada;

  const ficha = catalogo.find((f) => f.modelo === borrador.modelo) ?? null;
  const sinVersionVigente = efectivos.origen === 'FALLBACK';

  // 🔴 Sin ficha se falla CERRADO. Significa que el catálogo del backend no trae el modelo que la
  // pantalla tiene seleccionado — un desajuste que no se puede resolver adivinando, y publicar a
  // ciegas sería exactamente lo que el gate existe para impedir.
  if (!ficha) {
    return {
      ficha: null,
      hayCambios: false,
      valido: false,
      sinVersionVigente,
      modeloBloqueado: true,
      puedePublicar: false,
      textoBoton: 'Publicar',
      ayuda: 'El backend no reconoce este modelo. Recargá la pantalla antes de publicar.',
      advertencia: null,
    };
  }

  const aplican = ficha.parametros;

  // Sólo se comparan los parámetros que el modelo USA. Comparar los tres siempre daría "hay
  // cambios" por un campo que no va a viajar en el cuerpo — un botón habilitado por una
  // diferencia que la publicación no puede producir.
  const hayCambios =
    borrador.modelo !== efectivos.modelo ||
    aplican.some((p) => valorValido(p, borrador[p]) !== efectivos[p]);

  const valido = aplican.every((p) => valorValido(p, borrador[p]) !== null);
  const modeloBloqueado = !ficha.publicable;

  const puedePublicar =
    (hayCambios || sinVersionVigente) && valido && !modeloBloqueado && !publicando;

  // "Guardar" da por supuesto que hay algo escrito encima de una versión que ya existe. Sin
  // versión vigente eso es falso: no se guarda una edición, se publica la primera versión.
  const textoBoton = publicando
    ? 'Publicando…'
    : sinVersionVigente
      ? `Publicar como versión ${proximaVersion}`
      : `Guardar como versión ${proximaVersion}`;

  // El orden va del obstáculo más fundamental al más leve: si el modelo está bloqueado, decirle
  // además que le falta completar un campo lo manda a llenar un formulario que igual se va a
  // rechazar.
  const ayuda = modeloBloqueado
    ? `Todavía no se puede publicar: falta ${ficha.falta ?? 'construirlo'}.`
    : !valido
      ? `Completá ${aplican
          .filter((p) => valorValido(p, borrador[p]) === null)
          .map((p) => CAMPOS[p].etiqueta.toLowerCase())
          .join(' y ')}.`
      : hayCambios
        ? null
        : sinVersionVigente
          ? `No hay ninguna versión vigente: publicar deja este modelo guardado como la versión ${proximaVersion}, y desde ahí los viajes quedan atribuidos a ella.`
          : 'Sin cambios que guardar.';

  return {
    ficha,
    hayCambios,
    valido,
    sinVersionVigente,
    modeloBloqueado,
    puedePublicar,
    textoBoton,
    ayuda,
    advertencia: ficha.advertencia,
  };
}

/**
 * Arma el cuerpo del `POST` con **exactamente** los parámetros que el modelo usa.
 *
 * 🔴 Los que no aplican NO se mandan, ni como `null` ni como `undefined` explícito: el backend
 * rechaza con 400 cualquier parámetro que el modelo no use, porque guardarlo dejaría en la tabla
 * un número que no gobierna nada y que después se lee como si gobernara algo. Se testea aparte
 * porque es la mitad del contrato que el formulario tiene que respetar — la otra mitad (qué
 * campos mostrar) sale de la misma `ficha.parametros`.
 */
export function armarPublicacion(
  borrador: BorradorPrecio,
  ficha: FichaModelo,
  motivo: string,
): PublicarPolitica {
  const cuerpo: PublicarPolitica = { modelo: borrador.modelo, motivo };
  for (const p of ficha.parametros) {
    const valor = valorValido(p, borrador[p]);
    if (valor !== null) cuerpo[p] = valor;
  }
  return cuerpo;
}

/** El borrador inicial, copiado de lo que los flujos están usando AHORA. */
export function borradorDe(efectivos: PoliticaEfectiva): BorradorPrecio {
  const texto = (valor: number | null): string => (valor === null ? '' : String(valor));
  return {
    modelo: efectivos.modelo,
    ventanaPrecioSeg: texto(efectivos.ventanaPrecioSeg),
    maxOfertasPrecio: texto(efectivos.maxOfertasPrecio),
    toleranciaTechoPct: texto(efectivos.toleranciaTechoPct),
  };
}
